import { normalizeMethodPayload } from "@/api/healthcareApi.utils";
import { HttpClient } from "@/services/httpClient";
import { Controllers, FrappeMethodType } from "@/type/api";
import { WORKSPACE_USE_MOCKS } from "@/modules/healthcare/config";
import {
  mockAddWorkspaceNote,
  mockAttachWorkspaceFile,
  mockGetMyWorkspace,
  mockGetWorkspaceRecord,
  mockPerformWorkspaceAction,
  mockRemoveWorkspaceAttachment,
} from "@/modules/healthcare/mock/workspace";

const workspaceService = new HttpClient(Controllers.vetVisit);

export const WORKSPACE_METHODS = {
  getMyWorkspace: "pet_app.api.workspace.get_my_workspace",
  getRecord: "pet_app.api.workspace.get_record",
  performAction: "pet_app.api.workspace.perform_action",
  addNote: "pet_app.api.workspace.add_note",
  attachFile: "pet_app.api.workspace.attach_file",
  removeAttachment: "pet_app.api.workspace.remove_attachment",
} as const;

export type WorkspaceBackendMode =
  | "doctor"
  | "service"
  | "coordinator"
  | "diagnostics"
  | "accounting"
  | "management"
  | "all";

export type WorkspaceSourceType =
  | "Visit"
  | "Case Sheet"
  | "Appointment"
  | "Service"
  | "Procedure"
  | "Lab"
  | "Radiology"
  | "Imaging"
  | "Invoice"
  | "Payment"
  | string;

export type WorkspacePriority = "Low" | "Normal" | "Urgent" | "Emergency" | string;

export interface WorkspacePartySummary {
  id?: string;
  name?: string;
  name_label?: string;
  full_name?: string;
  pet_name?: string;
  species?: string;
  breed?: string;
  gender?: string;
  weight?: number;
  phone?: string;
  email?: string;
  customer_id?: string;
  image?: string;
}

export interface WorkspaceAssigneeSummary {
  id?: string;
  name?: string;
  name_label?: string;
  user?: string;
  specialization?: string;
  image?: string;
}

export interface WorkspaceMetrics {
  total: number;
  overdue: number;
  urgent: number;
  by_source: Record<string, number>;
  by_status: Record<string, number>;
}

export interface WorkspaceItemContract {
  id: string;
  name: string;
  source_type: WorkspaceSourceType;
  source_doctype?: string;
  title: string;
  subtitle?: string;
  status?: string | null;
  priority?: WorkspacePriority | null;
  pet?: WorkspacePartySummary | string | null;
  guardian?: WorkspacePartySummary | string | null;
  assignee?: WorkspaceAssigneeSummary | string | null;
  scheduled_at?: string | null;
  due_at?: string | null;
  overdue?: boolean;
  next_task?: string | null;
  modified?: string | null;
  creation?: string | null;
  badges?: string[];
  actions?: string[];
  available_actions?: string[];
  visit_id?: string | null;
  order_id?: string | null;
  care_service_id?: string | null;
  case_sheet_id?: string | null;
  sales_invoice?: string | null;
  follow_up_status?: string | null;
  follow_up_visit_id?: string | null;
  follow_up_of_visit_id?: string | null;
  procedure_template?: string | null;
  [key: string]: unknown;
}

export type WorkspaceQueueItemContract = WorkspaceItemContract;

export interface WorkspaceResponse {
  mode: string;
  user: string;
  metrics: WorkspaceMetrics;
  items: WorkspaceItemContract[];
  next_cursor: number | null;
  cursor: number | null;
  total: number;
}

export interface WorkspaceDiagnosis {
  id?: string;
  name?: string;
  disease?: string | null;
  diagnosis_text?: string | null;
  diagnosisText?: string | null;
  is_primary?: boolean | number;
  isPrimary?: boolean;
  severity?: string | null;
  note?: string | null;
  [key: string]: unknown;
}

export interface WorkspaceOrder {
  id?: string;
  name?: string;
  order_id?: string | null;
  orderId?: string | null;
  kind?: string;
  order_type?: string | null;
  title?: string;
  status?: string | null;
  priority?: string | null;
  qty?: number | null;
  price?: number | null;
  rate?: number | null;
  amount?: number | null;
  note?: string | null;
  item_code?: string | null;
  itemCode?: string | null;
  template_id?: string | null;
  templateId?: string | null;
  billable?: boolean;
  linked_doctype?: string | null;
  linkedDoctype?: string | null;
  linked_name?: string | null;
  linkedName?: string | null;
  [key: string]: unknown;
}

export interface WorkspaceLinkedRecord {
  id?: string;
  name?: string;
  kind?: string;
  source_type?: string;
  title?: string;
  status?: string | null;
  priority?: string | null;
  summary?: string | null;
  linked_doctype?: string | null;
  linkedDoctype?: string | null;
  linked_name?: string | null;
  linkedName?: string | null;
  [key: string]: unknown;
}

export interface WorkspaceDraftInvoiceLine {
  id?: string;
  item_name?: string;
  itemName?: string;
  item_type?: string | null;
  itemType?: string | null;
  qty: number;
  rate: number;
  amount: number;
  source?: string;
}

export interface WorkspaceBillingSnapshot {
  status?: string | null;
  currency?: string | null;
  total?: number | null;
  paid?: number | null;
  balance?: number | null;
  draft_invoice_lines?: WorkspaceDraftInvoiceLine[];
  draftInvoiceLines?: WorkspaceDraftInvoiceLine[];
  billable_items?: WorkspaceDraftInvoiceLine[];
  [key: string]: unknown;
}

export interface WorkspaceNote {
  id?: string;
  name?: string;
  note?: string | null;
  content?: string | null;
  text?: string | null;
  owner?: string | null;
  creation?: string | null;
  modified?: string | null;
  [key: string]: unknown;
}

export interface WorkspaceAttachmentContract {
  id?: string;
  name?: string;
  file_name?: string;
  type?: string | null;
  content_type?: string | null;
  size?: string | number | null;
  file_size?: string | number | null;
  url?: string | null;
  file_url?: string | null;
  isImage?: boolean;
  is_image?: boolean;
  addedAt?: string | null;
  creation?: string | null;
  [key: string]: unknown;
}

export interface WorkspaceTimelineEvent {
  title: string;
  meta?: string | null;
  color?: string | null;
  icon?: string | null;
  creation?: string | null;
  [key: string]: unknown;
}

export interface WorkspaceRecordDetail {
  summary?: {
    name?: string;
    source_type?: string;
    source_doctype?: string;
    title?: string;
    status?: string;
    priority?: string;
    case_sheet_id?: string;
    visit_datetime?: string;
    next_task?: string;
    creation?: string;
    modified?: string;
    [key: string]: unknown;
  };
  pet?: WorkspacePartySummary;
  guardian?: WorkspacePartySummary;
  assignee?: WorkspaceAssigneeSummary;
  clinical?: {
    chief_complaint?: string | null;
    chiefComplaint?: string | null;
    intake_summary?: string | null;
    intakeSummary?: string | null;
    overview?: string | null;
    examination?: string | null;
    assessment?: string | null;
    diagnosis?: string | null;
    plan?: string | null;
    instructions?: string | null;
    doctor_notes?: string | null;
    [key: string]: unknown;
  };
  follow_up?: Record<string, unknown>;
  consult_requests?: unknown[];
  diagnoses?: WorkspaceDiagnosis[];
  orders?: WorkspaceOrder[];
  linked_records?: WorkspaceLinkedRecord[];
  linkedRecords?: WorkspaceLinkedRecord[];
  notes?: Array<WorkspaceNote | string>;
  attachments?: WorkspaceAttachmentContract[];
  billing?: WorkspaceBillingSnapshot;
  timeline?: WorkspaceTimelineEvent[];
  raw?: { doctype: string; name: string };
  focus_source?: Record<string, unknown>;
  pending_follow_up_appointment?: string;

  // Normalized fields used by the profile workspace UI.
  sourceType?: string;
  name?: string;
  title?: string | null;
  subtitle?: string | null;
  status?: string | null;
  priority?: string | null;
}

export interface GetMyWorkspaceInput {
  mode: WorkspaceBackendMode;
  user?: string | null;
  search?: string | null;
  priority?: string | null;
  status?: string | null;
  limit?: number;
  cursor?: number | null;
}

export interface GetWorkspaceRecordInput {
  source_type: WorkspaceSourceType;
  name: string;
}

export interface WorkspaceActionInput {
  source_type: WorkspaceSourceType;
  name: string;
  action: string;
  payload?: Record<string, unknown>;
}

export interface WorkspaceAttachFileInput {
  source_type: WorkspaceSourceType;
  name: string;
  payload: Record<string, unknown>;
}

export interface WorkspaceRemoveAttachmentInput {
  source_type: WorkspaceSourceType;
  name: string;
  file_id: string;
}

async function callWorkspaceMethod<T>(
  method: string,
  input: Record<string, unknown>,
  verb: "get" | "post" = "get",
): Promise<T> {
  const response = verb === "post"
    ? await workspaceService.post<any, typeof input>(
        input,
        method,
        {},
        { headers: { "X-Suppress-Action-Loader": "1" } },
        FrappeMethodType.method,
      )
    : await workspaceService.get<any>(
        method,
        {},
        input,
        undefined,
        { headers: { "X-Suppress-Action-Loader": "1" } },
        FrappeMethodType.method,
      );

  return normalizeMethodPayload<T>(response);
}

export function GetMyWorkspace(input: GetMyWorkspaceInput) {
  if (WORKSPACE_USE_MOCKS)
    return Promise.resolve(mockGetMyWorkspace(input));

  return callWorkspaceMethod<WorkspaceResponse>(
    WORKSPACE_METHODS.getMyWorkspace,
    input as unknown as Record<string, unknown>,
  );
}

export function GetWorkspaceRecord(input: GetWorkspaceRecordInput) {
  if (WORKSPACE_USE_MOCKS)
    return Promise.resolve(mockGetWorkspaceRecord(input));

  return callWorkspaceMethod<WorkspaceRecordDetail>(
    WORKSPACE_METHODS.getRecord,
    input as unknown as Record<string, unknown>,
  );
}

export function PerformWorkspaceAction(input: WorkspaceActionInput) {
  if (WORKSPACE_USE_MOCKS)
    return Promise.resolve(mockPerformWorkspaceAction(input));

  return callWorkspaceMethod<WorkspaceRecordDetail>(
    WORKSPACE_METHODS.performAction,
    input as unknown as Record<string, unknown>,
    "post",
  );
}

export function AddWorkspaceNote(input: Omit<WorkspaceActionInput, "action" | "payload"> & { note: string }) {
  if (WORKSPACE_USE_MOCKS)
    return Promise.resolve(mockAddWorkspaceNote(input));

  return callWorkspaceMethod<WorkspaceRecordDetail>(
    WORKSPACE_METHODS.addNote,
    input as unknown as Record<string, unknown>,
    "post",
  );
}

export function AttachWorkspaceFile(input: WorkspaceAttachFileInput) {
  if (WORKSPACE_USE_MOCKS)
    return Promise.resolve(mockAttachWorkspaceFile(input));

  return callWorkspaceMethod<WorkspaceRecordDetail>(
    WORKSPACE_METHODS.attachFile,
    input as unknown as Record<string, unknown>,
    "post",
  );
}

export function RemoveWorkspaceAttachment(input: WorkspaceRemoveAttachmentInput) {
  if (WORKSPACE_USE_MOCKS)
    return Promise.resolve(mockRemoveWorkspaceAttachment(input));

  return callWorkspaceMethod<WorkspaceRecordDetail>(
    WORKSPACE_METHODS.removeAttachment,
    input as unknown as Record<string, unknown>,
    "post",
  );
}
